import { Button, Checkbox, LockKeyholeIcon, Typography } from '@/components/atoms';
import { ChevronRightIcon } from '@/components/atoms/icons';
import { NumberPad, OTPInput } from '@/components/molecules';
import { ConfirmationDialog } from '@/components/molecules/dialogs/ConfirmationDialog';
import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { navigateToWelcomeAfterLogout } from '@/core/navigation/logoutNavigation';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { passcodeStorage } from '@/core/storage/passcodeStorage';
import { useAppStore } from '@/core/stores/appStore';
import { biometricAuth } from '@/core/wrappers/biometricAuth';
import { router } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePasscode } from '../hooks/usePasscode';

export type PasscodeMode = 'setup' | 'change' | 'verify';

export interface PasscodeScreenProps {
    mode?: PasscodeMode;
    onPasscodeComplete?: (passcode: string) => void;
    onBackPress?: () => void;
    onSuccess?: () => void;
    title?: string;
    subtitle?: string;
    maxLength?: number;
    hideHeaderLogo?: boolean;
    from?: string;
}

type SetupStep = 'enter' | 'confirm';
type ChangeStep = 'old' | 'new' | 'confirm';

export const PasscodeScreen = React.memo<PasscodeScreenProps>(function PasscodeScreen({
    mode = 'setup',
    onPasscodeComplete,
    onBackPress,
    onSuccess,
    title,
    subtitle,
    maxLength = 4,
    hideHeaderLogo = false,
    from,
}) {
    let passcodeViewAction = 'passcode_change_viewed';
    if (mode === 'setup') {
        passcodeViewAction = 'passcode_setup_viewed';
    } else if (mode === 'verify') {
        passcodeViewAction = 'passcode_verify_viewed';
    }
    useCatalogDynatraceView(DT_SCREEN.PASSCODE, passcodeViewAction, true);
    const { t } = useTranslation();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { isRTL } = useI18nContext();
    const {
        setupPasscode,
        changePasscode,
        skipPasscode,
        verifyPasscode,
        resetPasscode,
        isLoading,
        error,
    } = usePasscode();
    const clearAuth = useAppStore((state) => state.clearAuth);

    // State for setup mode
    const [setupStep, setSetupStep] = useState<SetupStep>('enter');
    const [initialPasscode, setInitialPasscode] = useState('');
    const [confirmPasscode, setConfirmPasscode] = useState('');

    // State for change mode
    const [changeStep, setChangeStep] = useState<ChangeStep>('old');
    const [oldPasscode, setOldPasscode] = useState('');
    const [newPasscode, setNewPasscode] = useState('');
    const [newConfirmPasscode, setNewConfirmPasscode] = useState('');

    // State for verify mode
    const [verifyPasscodeValue, setVerifyPasscodeValue] = useState('');

    // Biometric state
    const [biometricEnabled, setBiometricEnabled] = useState(false);
    const [biometricAvailable, setBiometricAvailable] = useState(false);

    // Dialog state
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogOnOk, setDialogOnOk] = useState<(() => void) | null>(null);
    const [confirmResetVisible, setConfirmResetVisible] = useState(false);

    // Check biometric availability on mount
    useEffect(() => {
        const checkBiometric = async () => {
            const available = await biometricAuth.isBiometricAvailable();
            setBiometricAvailable(available);
        };
        if (mode === 'setup') {
            void checkBiometric();
        }
    }, [mode]);

    // Handle biometric toggle: when enabling, invoke biometric immediately and persist state
    const handleToggleBiometric = async (): Promise<void> => {
        if (!biometricAvailable) {
            setDialogMessage(t('profile.passcodeEnableBiometric'));
            setDialogOnOk(() => () => {
                setDialogVisible(false);
            });
            setDialogVisible(true);
            return;
        }
        if (!biometricEnabled) {
            const result = await biometricAuth.authenticateWithBiometric();
            if (result.success) {
                setBiometricEnabled(true);
                try {
                    await passcodeStorage.setBiometricEnabled(true);
                } catch {
                    // storage error already logged in wrapper
                }
            } else {
                setDialogMessage(t('profile.passcodeVerifyError'));
                setDialogOnOk(() => () => {
                    setDialogVisible(false);
                });
                setDialogVisible(true);
                setBiometricEnabled(false);
                try {
                    await passcodeStorage.setBiometricEnabled(false);
                } catch {
                    // storage error already logged
                }
            }
        } else {
            setBiometricEnabled(false);
            try {
                await passcodeStorage.setBiometricEnabled(false);
                // Clear stored passcode when biometric is disabled
                await passcodeStorage.clearPasscode();
            } catch {
                // storage error already logged
            }
        }
    };

    // Get current passcode value based on mode and step
    const getCurrentPasscode = (): string => {
        if (mode === 'setup') {
            return setupStep === 'enter' ? initialPasscode : confirmPasscode;
        } else if (mode === 'change') {
            if (changeStep === 'old') return oldPasscode;
            if (changeStep === 'new') return newPasscode;
            return newConfirmPasscode;
        } else {
            return verifyPasscodeValue;
        }
    };

    // Set current passcode value based on mode and step
    const setCurrentPasscode = (value: string): void => {
        if (mode === 'setup') {
            if (setupStep === 'enter') {
                setInitialPasscode(value);
            } else {
                setConfirmPasscode(value);
            }
        } else if (mode === 'change') {
            if (changeStep === 'old') {
                setOldPasscode(value);
            } else if (changeStep === 'new') {
                setNewPasscode(value);
            } else {
                setNewConfirmPasscode(value);
            }
        } else {
            setVerifyPasscodeValue(value);
        }
    };

    // Get title based on mode and step
    const getTitle = (): string => {
        if (title) return title;

        if (mode === 'setup') {
            return setupStep === 'enter'
                ? t('profile.passcodeEnterPasscode')
                : t('profile.passcodeConfirmPasscode');
        } else if (mode === 'change') {
            if (changeStep === 'old') return t('profile.passcodeEnterOldPasscode');
            if (changeStep === 'new') return t('profile.passcodeEnterNewPasscode');
            return t('profile.passcodeConfirmPasscode');
        } else {
            return t('profile.passcodeEnterPasscode');
        }
    };

    const handleNumberPress = (number: string | number) => {
        const current = getCurrentPasscode();
        if (current.length < maxLength) {
            setCurrentPasscode(current + number.toString());
        }
    };

    const handleDeletePress = () => {
        if (isLoading) return; // Prevent delete during loading
        const current = getCurrentPasscode();
        if (current.length > 0) {
            setCurrentPasscode(current.slice(0, -1));
        }
    };

    const handleSubmit = async () => {
        const current = getCurrentPasscode();
        if (current.length !== maxLength) return;

        try {
            if (mode === 'setup') {
                if (setupStep === 'enter') {
                    // Move to confirm step
                    setSetupStep('confirm');
                    setConfirmPasscode('');
                } else {
                    // Confirm passcode matches
                    if (initialPasscode !== confirmPasscode) {
                        setDialogMessage(t('profile.passcodeMismatch'));
                        setDialogOnOk(() => () => {
                            // Reset to initial enter step on mismatch
                            setDialogVisible(false);
                            setSetupStep('enter');
                            setInitialPasscode('');
                            setConfirmPasscode('');
                        });
                        setDialogVisible(true);
                        return;
                    }

                    // Set passcode via API
                    await setupPasscode(confirmPasscode, biometricEnabled);

                    // After setup, verify passcode to get passcode token
                    logger.info('PasscodeScreen', 'Passcode set, verifying to get passcode token');
                    const verifyResult = await verifyPasscode(confirmPasscode);
                    
                    if (!verifyResult.success) {
                        // Verification failed - show error but still allow navigation
                        logger.warn('PasscodeScreen', 'Passcode verification failed after setup', verifyResult);
                    } else {
                        logger.info('PasscodeScreen', 'Passcode verified successfully, token stored');
                    }

                    // Show success and call success callback
                    setDialogMessage(t('profile.passcodeSetSuccess'));
                    setDialogOnOk(() => {
                        setDialogVisible(false);
                        onSuccess?.();
                        onPasscodeComplete?.(confirmPasscode);
                        // Defer navigation to avoid React render warning
                        setTimeout(() => {
                            if (onBackPress) {
                                onBackPress();
                            } else {
                                // Navigate based on `from` parameter
                                if (from === 'privacy') {
                                    // From profile - navigate back to privacy/security settings
                                    router.back();
                                } else if (from === 'login') {
                                    // From first-time login - navigate to home
                                    try {
                                        if (router.canGoBack()) {
                                            router.dismissAll();
                                        }
                                    } catch (error) {
                                        // Ignore dismissAll errors - replace will handle navigation
                                        logger.debug('PasscodeScreen', 'dismissAll failed, using replace only', error);
                                    }
                                    router.replace('/(auth)/(home)');
                                } else {
                                    // Default - go back
                                    router.back();
                                }
                            }
                        }, 0);
                    });
                    setDialogVisible(true);
                }
            } else if (mode === 'change') {
                if (changeStep === 'old') {
                    // Verify old passcode (we'll verify it when changing)
                    setChangeStep('new');
                    setNewPasscode('');
                } else if (changeStep === 'new') {
                    // Move to confirm step
                    setChangeStep('confirm');
                    setNewConfirmPasscode('');
                } else {
                    // Confirm new passcode matches
                    if (newPasscode !== newConfirmPasscode) {
                        setDialogMessage(t('profile.passcodeMismatch'));
                        setDialogOnOk(() => () => {
                            setDialogVisible(false);
                            // Go back to "enter new passcode" step on mismatch
                            setChangeStep('new');
                            setNewPasscode('');
                            setNewConfirmPasscode('');
                        });
                        setDialogVisible(true);
                        return;
                    }

                    // Change passcode via API
                    await changePasscode(oldPasscode, newConfirmPasscode);

                    // Show success and call success callback
                    setDialogMessage(t('profile.passcodeChangeSuccess'));
                    setDialogOnOk(() => {
                        setDialogVisible(false);
                        onSuccess?.();
                        // Defer navigation to avoid React render warning
                        setTimeout(() => {
                            if (onBackPress) {
                                onBackPress();
                            } else {
                                router.back();
                            }
                        }, 0);
                    });
                    setDialogVisible(true);
                }
            } else {
                // Verify mode
                const result = await verifyPasscode(current);
                if (result.success) {
                    onPasscodeComplete?.(current);
                    onSuccess?.();
                } else {
                    setDialogMessage(t('profile.passcodeVerifyError'));
                    setDialogOnOk(() => () => {
                        setDialogVisible(false);
                        setVerifyPasscodeValue('');
                    });
                    setDialogVisible(true);
                }
            }
        } catch (err) {
            // Show error message to user
            let errorMessage: string;
            if (mode === 'setup') {
                errorMessage = err instanceof Error ? err.message : (error || t('profile.passcodeSetError'));
            } else if (mode === 'change') {
                errorMessage = err instanceof Error ? err.message : (error || t('profile.passcodeChangeError'));
            } else {
                errorMessage = err instanceof Error ? err.message : (error || t('profile.passcodeVerifyError'));
            }
            
            setDialogMessage(errorMessage);
            setDialogOnOk(() => () => {
                setDialogVisible(false);
                // Reset passcode based on mode and step
                if (mode === 'setup') {
                    if (setupStep === 'confirm') {
                        // Go back to enter step on error
                        setSetupStep('enter');
                        setConfirmPasscode('');
                        setInitialPasscode('');
                    } else {
                        setInitialPasscode('');
                    }
                } else if (mode === 'change') {
                    if (changeStep === 'confirm') {
                        // Go back to old passcode step on error (old passcode might be wrong)
                        setChangeStep('old');
                        setOldPasscode('');
                        setNewPasscode('');
                        setNewConfirmPasscode('');
                    } else if (changeStep === 'new') {
                        // Go back to old passcode step
                        setChangeStep('old');
                        setOldPasscode('');
                        setNewPasscode('');
                    } else {
                        setOldPasscode('');
                    }
                } else {
                    setVerifyPasscodeValue('');
                }
            });
            setDialogVisible(true);
        }
    };

    const handleSkip = async () => {
        trackCatalogEvent({
            screen: DT_SCREEN.PASSCODE,
            actionName: 'passcode_biometric_skip',
            eventType: 'action',
        });
        try {
            // Use `from` parameter or default to 'login'
            const fromParam = from || 'login';
            // Map 'privacy' to 'profile' for API call (backend enum expects 'profile' | 'login')
            // Keep 'privacy' for navigation logic below
            const apiFrom = fromParam === 'privacy' ? 'profile' : fromParam;
            await skipPasscode(apiFrom);
            // No confirmation dialog - navigate directly
            onSuccess?.();
            setTimeout(() => {
                if (onBackPress) {
                    onBackPress();
                } else {
                    // Navigate based on `from` parameter
                    if (fromParam === 'privacy') {
                        // From profile - navigate back to privacy/security settings
                        router.back();
                    } else if (fromParam === 'login') {
                        // From first-time login - navigate to home
                        try {
                            if (router.canGoBack()) {
                                router.dismissAll();
                            }
                        } catch (error) {
                            // Ignore dismissAll errors - replace will handle navigation
                            logger.debug('PasscodeScreen', 'dismissAll failed, using replace only', error);
                        }
                        router.replace('/(auth)/(home)');
                    } else {
                        // Default - go back
                        router.back();
                    }
                }
            }, 0);
        } catch (err) {
            // Show error message to user
            const errorMessage = err instanceof Error ? err.message : (error || t('profile.passcodeSkipError'));
            setDialogMessage(errorMessage);
            setDialogOnOk(() => {
                setDialogVisible(false);
            });
            setDialogVisible(true);
        }
    };

    const handleResetPasscode = () => {
        // Show confirmation: resetting will log the user out and require re-login
        setConfirmResetVisible(true);
    };

    const handleConfirmReset = async () => {
        setConfirmResetVisible(false);
        try {
            const result = await resetPasscode();
            const message = result.success
                ? result.message || t('profile.passcodeResetSuccess')
                : result.message || t('profile.passcodeResetError');
            setDialogMessage(message);
            setDialogOnOk(() => () => {
                setDialogVisible(false);
                // Reset should log out; use shared logout navigation helper
                void clearAuth().then(() => navigateToWelcomeAfterLogout());
            });
        } catch (err) {
            const message = t('profile.passcodeResetError');
            setDialogMessage(message);
            setDialogOnOk(() => () => {
                setDialogVisible(false);
            });
        } finally {
            setDialogVisible(true);
        }
    };

    const handleCancelReset = () => {
        setConfirmResetVisible(false);
    };

    const currentPasscode = getCurrentPasscode();
    const showBiometric = mode === 'setup' && biometricAvailable;
    const showSkip = mode === 'setup' && setupStep === 'enter';
    const showReset = mode === 'change' && changeStep === 'old';

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            {/* Header */}
            <LogoHeader
                {...(hideHeaderLogo ? { showBackButton: true } : {})}
                onBackPress={onBackPress}
                hideLogo={hideHeaderLogo}
                showLanguageSwitch={!hideHeaderLogo}
            />

            {/* Content */}
            <View style={[styles.content, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 24 }]}>
                {/* Page Header (icon + title) - left aligned per Figma */}
                <View style={styles.pageHeader}>
                    <View style={styles.iconRow}>
                        <LockKeyholeIcon size={40} color={colors.fontPrimary} />
                    </View>
                    <Typography variant="title1" color="fontPrimary" style={styles.title}>
                        {getTitle()}
                    </Typography>
                </View>

                {/* Passcode Boxes + Enable biometric checkbox (same container for alignment) */}
                <View style={styles.passcodeSection}>
                    <OTPInput
                        length={4}
                        value={currentPasscode}
                        onChangeText={(val) => setCurrentPasscode(val)}
                        onComplete={(val) => {
                            setCurrentPasscode(val);
                            void handleSubmit();
                        }}
                        secure={true}
                        focusable={false}
                        autoFocus={false}
                        loading={isLoading}
                        disabled={isLoading}
                    />
                    {/* Enable biometric checkbox - in setup mode, both enter and confirm steps */}
                    {showBiometric && (
                        <View style={styles.biometricRow}>
                            <Checkbox
                                isSelected={biometricEnabled}
                                onPress={handleToggleBiometric}
                                size={18}
                            />
                            <Typography variant="body" color="fontPrimary">
                                {t('profile.passcodeEnableBiometric')}
                            </Typography>
                        </View>
                    )}
                </View>

                {/* Reset passcode link - only in change mode, old step */}
                {showReset && (
                    <View style={styles.forgotRow}>
                        <Button
                            title={t('profile.passcodeResetPasscode')}
                            variant="text"
                            trailing={
                                <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                                    <ChevronRightIcon size={18} />
                                </View>
                            }
                            onPress={handleResetPasscode}
                        />
                    </View>
                )}

                {/* Skip passcode link - only in setup mode, enter step */}
                {showSkip && (
                    <View style={styles.skipRow}>
                        <Button
                            title={t('profile.passcodeSkipPasscode')}
                            variant="text"
                            trailing={
                                <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                                    <ChevronRightIcon size={18} />
                                </View>
                            }
                            onPress={handleSkip}
                            disabled={isLoading}
                        />
                    </View>
                )}

                {/* Spacer to push keypad to bottom */}
                <View style={styles.flexSpacer} />

                {/* Number Pad pinned near bottom per Figma spacing - responsive */}
                <View style={styles.numberPadSection}>
                    <NumberPad
                        onNumberPress={handleNumberPress}
                        onDeletePress={handleDeletePress}
                        onSubmitPress={handleSubmit}
                        disabled={isLoading}
                        loading={isLoading}
                    />
                </View>
            </View>

            {/* Success/Error Dialog */}
            <OkDialog
                visible={dialogVisible}
                message={dialogMessage}
                onOk={() => {
                    if (dialogOnOk) {
                        dialogOnOk();
                    } else {
                        setDialogVisible(false);
                    }
                }}
            />

            <ConfirmationDialog
                visible={confirmResetVisible}
                message={t('profile.passcodeResetConfirmation')}
                onConfirm={handleConfirmReset}
                onCancel={handleCancelReset}
            />
        </View>
    );
});

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    content: {
        flex: 1,
        justifyContent: 'flex-start',
        paddingHorizontal: 16,
    },
    pageHeader: {
        gap: 16,
        marginBottom: 24,
    },
    iconRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    title: {
        textAlign: 'left',
        fontSize: 28,
        fontWeight: '500',
    },
    passcodeSection: {
        alignItems: 'center',
        marginTop: 24,
    },
    biometricRow: {
        marginTop: 16,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center', // Center the row
        gap: 4, // 4px gap as per Figma
        marginLeft: -16, // Negative margin to align with first passcode box (matches OTPInput internal padding)
    },
    forgotRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginTop: 12,
        minHeight: 48,
        paddingBottom: 4,
    },
    skipRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginTop: 12,
        minHeight: 48, // Ensure button has enough space
    },
    flexSpacer: {
        flexGrow: 1,
        minHeight: 8, // Minimum space, but can grow
    },
    numberPadSection: {
        alignItems: 'center',
        marginBottom: 35,
    },
});

export default PasscodeScreen;

